import { useState, useEffect, useCallback } from "react";
import { ShieldBan, ShieldCheck, Plus, Search, RefreshCw, Loader2, Trash2, AlertTriangle, Clock, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { tokenStore } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    + " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const secs = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

export default function IpManagement() {
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [unblocking, setUnblocking] = useState(null);

  // Block form state
  const [newIp, setNewIp] = useState("");
  const [newReason, setNewReason] = useState("");
  const [blocking, setBlocking] = useState(false);
  const [showBlockForm, setShowBlockForm] = useState(false);

  const fetchBlockedIPs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/v1/admin/blocked-ips`, {
        headers: { Authorization: `Bearer ${tokenStore.getAccess()}` },
      });
      if (res.ok) setBlockedIPs(await res.json());
    } catch { toast.error("Failed to load blocked IPs"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBlockedIPs(); }, [fetchBlockedIPs]);

  const handleBlock = async (e) => {
    e.preventDefault();
    const ip = newIp.trim();
    if (!ip) return;
    // Basic IP validation
    const ipv4 = /^(\d{1,3}\.){3}\d{1,3}$/;
    const ipv6 = /^[0-9a-fA-F:]+$/;
    if (!ipv4.test(ip) && !ipv6.test(ip)) {
      toast.error("Enter a valid IPv4 or IPv6 address");
      return;
    }
    if (blockedIPs.some(b => b.ip === ip)) {
      toast.warning(`${ip} is already blocked`);
      return;
    }
    setBlocking(true);
    try {
      const res = await fetch(`${API}/api/v1/admin/block-ip`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tokenStore.getAccess()}` },
        body: JSON.stringify({ ip, reason: newReason.trim() || "Manual block from IP Management" }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setBlockedIPs(prev => [{ ip, reason: newReason.trim() || "Manual block from IP Management", blocked_at: data.blocked_at, blocked_by_email: "You" }, ...prev]);
      setNewIp(""); setNewReason(""); setShowBlockForm(false);
      toast.success(`${ip} has been blocked`);
    } catch { toast.error("Failed to block IP"); }
    finally { setBlocking(false); }
  };

  const handleUnblock = async (ip) => {
    setUnblocking(ip);
    try {
      const res = await fetch(`${API}/api/v1/admin/block-ip/${encodeURIComponent(ip)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${tokenStore.getAccess()}` },
      });
      if (!res.ok) throw new Error();
      setBlockedIPs(prev => prev.filter(b => b.ip !== ip));
      toast.success(`${ip} has been unblocked`);
    } catch { toast.error(`Failed to unblock ${ip}`); }
    finally { setUnblocking(null); }
  };

  const filtered = blockedIPs.filter(b =>
    !search ||
    b.ip.includes(search) ||
    (b.reason || "").toLowerCase().includes(search.toLowerCase()) ||
    (b.blocked_by_email || "").toLowerCase().includes(search.toLowerCase())
  );

  // Stats
  const now = Date.now();
  const last24h = blockedIPs.filter(b => b.blocked_at && (now - new Date(b.blocked_at)) < 86400000).length;
  const last7d  = blockedIPs.filter(b => b.blocked_at && (now - new Date(b.blocked_at)) < 604800000).length;

  return (
    <div data-testid="ip-management-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="ip-management-title">
            IP Blocklist Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Block and unblock IP addresses from accessing the platform
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchBlockedIPs}
            disabled={loading}
            className="inline-flex items-center gap-2 border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors text-xs px-4 py-2 disabled:opacity-50"
            data-testid="refresh-ip-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={() => setShowBlockForm(v => !v)}
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-4 py-2 transition-colors"
            data-testid="add-block-ip-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Block IP Address
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 p-5 rounded" data-testid="stat-total-blocked">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-red-50 flex items-center justify-center rounded-full">
              <ShieldBan className="w-4.5 h-4.5 text-red-600 w-4 h-4" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{blockedIPs.length}</p>
              <p className="text-xs text-slate-500">Total Blocked IPs</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 p-5 rounded" data-testid="stat-blocked-24h">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-amber-50 flex items-center justify-center rounded-full">
              <Clock className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{last24h}</p>
              <p className="text-xs text-slate-500">Blocked in Last 24h</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 p-5 rounded" data-testid="stat-blocked-7d">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-50 flex items-center justify-center rounded-full">
              <AlertTriangle className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{last7d}</p>
              <p className="text-xs text-slate-500">Blocked in Last 7 Days</p>
            </div>
          </div>
        </div>
      </div>

      {/* Block IP Form */}
      {showBlockForm && (
        <div className="bg-red-50 border border-red-200 rounded p-5 mb-6" data-testid="block-ip-form">
          <h3 className="text-sm font-semibold text-red-800 mb-4 flex items-center gap-2">
            <ShieldBan className="w-4 h-4" /> Block IP Address
          </h3>
          <form onSubmit={handleBlock} className="flex flex-col sm:flex-row gap-3">
            <Input
              placeholder="IP Address (e.g. 192.168.1.100)"
              value={newIp}
              onChange={e => setNewIp(e.target.value)}
              className="font-mono text-sm flex-shrink-0 sm:w-52"
              data-testid="new-ip-input"
              required
            />
            <Input
              placeholder="Reason (optional)"
              value={newReason}
              onChange={e => setNewReason(e.target.value)}
              className="text-sm flex-1"
              data-testid="new-ip-reason-input"
            />
            <button
              type="submit"
              disabled={blocking || !newIp.trim()}
              className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-5 py-2 transition-colors disabled:opacity-50 flex-shrink-0"
              data-testid="confirm-block-ip-btn"
            >
              {blocking ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Blocking...</> : <><ShieldBan className="w-3.5 h-3.5" /> Block IP</>}
            </button>
            <button
              type="button"
              onClick={() => { setShowBlockForm(false); setNewIp(""); setNewReason(""); }}
              className="inline-flex items-center justify-center gap-2 border border-slate-300 text-slate-600 hover:bg-slate-50 text-xs px-4 py-2 transition-colors flex-shrink-0"
              data-testid="cancel-block-ip-btn"
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* Search + Table */}
      <div className="bg-white border border-slate-200 rounded">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <ShieldBan className="w-4 h-4 text-red-500" />
            Blocked IPs
            <Badge variant="outline" className="text-xs ml-1">{filtered.length}</Badge>
          </h2>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <Input
              placeholder="Search IP, reason or admin..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 text-xs h-8"
              data-testid="ip-search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading blocked IPs...
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400" data-testid="no-blocked-ips">
            <ShieldCheck className="w-10 h-10 mb-3 text-emerald-400" />
            <p className="text-sm font-medium text-slate-500">
              {search ? "No IPs match your search" : "No blocked IPs"}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {search ? "Try a different search term" : "The platform is open to all IPs"}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-5 py-3">IP Address</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-5 py-3">Reason</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-5 py-3">Blocked By</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-5 py-3">Blocked At</th>
                <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide px-5 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map((entry, i) => (
                <tr key={entry.ip} className="hover:bg-slate-50 transition-colors group" data-testid={`blocked-ip-row-${i}`}>
                  <td className="px-5 py-3.5">
                    <span className="font-mono text-sm font-medium text-slate-900 bg-red-50 border border-red-100 px-2 py-0.5 rounded">
                      {entry.ip}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-sm text-slate-600">{entry.reason || <span className="text-slate-400 italic">No reason given</span>}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <User className="w-3 h-3 text-slate-400" />
                      {entry.blocked_by_email || "—"}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="text-xs text-slate-600">{fmtDate(entry.blocked_at)}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{timeAgo(entry.blocked_at)}</div>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <button
                      onClick={() => handleUnblock(entry.ip)}
                      disabled={unblocking === entry.ip}
                      className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 px-3 py-1.5 transition-all disabled:opacity-50"
                      data-testid={`unblock-ip-btn-${i}`}
                    >
                      {unblocking === entry.ip ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> Unblocking...</>
                      ) : (
                        <><Trash2 className="w-3 h-3" /> Unblock</>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
